import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
} from "firebase/firestore";
import { db } from '../../src/firebaseConfig';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { ChevronDown, ChevronUp, TrendingUp, DollarSign, ShoppingCart, Database } from "lucide-react";

// ========= Helpers robustos =========
const parseAmount = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const s = String(v)
    .replace(/\s/g, "")
    .replace(/[,$]/g, "")
    .replace(/^\$/, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const parseDateSmart = (fechaStr) => {
  if (!fechaStr) return null;
  
  // Primero intentar con new Date directamente (funciona para ISO 8601)
  const directDate = new Date(fechaStr);
  if (!isNaN(directDate.getTime())) return directDate;
  
  // Si falla, intentar con formatos personalizados
  const s = String(fechaStr).trim().replace(/-/g, "/");

  let m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));

  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));

  return null;
};

const detectCurrencyByThreshold = (amountNumber) =>
  amountNumber < 100 ? "USD" : "COP";

// ========= Componente =========
const months = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

const thisYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }).map((_, i) => thisYear - i);

const CHART_COLORS = ['#3b82f6', '#60a5fa', '#93c5fd', '#2563eb', '#1d4ed8', '#10b981', '#ef4444', '#f59e0b'];

export default function StatisticsPage() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [receiptsData, setReceiptsData] = useState([]);
  const [ventasData, setVentasData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReceiptsList, setShowReceiptsList] = useState(false);
  const [showDiscrepancies, setShowDiscrepancies] = useState(false);

  // Suscripción a receipts
  useEffect(() => {
    const q = query(collection(db, "receipts"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = [];
        snap.forEach((doc) => arr.push({ id: doc.id, source: 'receipts', ...doc.data() }));
        setReceiptsData(arr);
      },
      (err) => {
        console.error("Error cargando receipts:", err);
      }
    );
    return () => unsub();
  }, []);

  // Suscripción a Ventas
  useEffect(() => {
    const q = query(collection(db, "Ventas"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = [];
        snap.forEach((doc) => arr.push({ id: doc.id, source: 'Ventas', ...doc.data() }));
        setVentasData(arr);
        setLoading(false);
      },
      (err) => {
        console.error("Error cargando Ventas:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const processed = useMemo(() => {
    if (!receiptsData?.length && !ventasData?.length)
      return {
        totalSalesUSD: 0,
        totalSalesCOP: 0,
        salesByTypeUSD: {},
        salesByTypeCOP: {},
        salesByPaymentMethodUSD: {},
        salesByPaymentMethodCOP: {},
        averageProductsPerReceipt: 0,
        currencyDiscrepancies: [],
        items: [],
        dailySales: [],
        sourceBreakdown: { receipts: 0, ventas: 0 },
      };

    // Normalizar RECEIPTS
    const normalizedReceipts = receiptsData
      .map((r) => {
        const amount = parseAmount(r?.valorCompra);
        const createdAtDate =
          r?.createdAt && typeof r.createdAt.toDate === "function"
            ? r.createdAt.toDate()
            : null;
        const parsedDate = createdAtDate || parseDateSmart(r?.fecha);
        return {
          ...r,
          _amount: amount,
          _date: parsedDate,
          _currency: detectCurrencyByThreshold(amount),
          _payment: r?.medioPago || "Sin especificar",
          _platform:
            r?.plataforma ||
            (Array.isArray(r?.productosComprados) &&
              r.productosComprados[0]?.plataforma) ||
            "Sin especificar",
          _source: 'receipts'
        };
      })
      .filter((r) => r._date);

    // Normalizar VENTAS (estructura exacta de Firebase)
    const normalizedVentas = ventasData
      .map((v) => {
        // Usar SOLO unitPrice como monto
        const amount = parseAmount(v?.unitPrice);
        // Parse saleDate que viene en formato ISO 8601
        const parsedDate = parseDateSmart(v?.saleDate);
        
        if (!parsedDate) {
          console.warn('Venta sin fecha válida:', v.id, v.saleDate);
        }
        
        return {
          ...v,
          _amount: amount,
          _date: parsedDate,
          _currency: detectCurrencyByThreshold(amount),
          _payment: "Sin especificar", // Ventas no tiene campo de medio de pago
          _platform: v?.saleType || "Sin especificar", // Corazon, Prime Video, etc.
          _source: 'Ventas',
          nombreComprador: v?.phoneNumber || v?.codigo || "N/A",
          cantidadProductos: v?.quantity || 1,
          // Campos adicionales útiles
          codigo: v?.codigo,
          perfil: v?.perfil,
          pin: v?.pin,
          investment: v?.investment,
          totalProfit: v?.totalProfit,
        };
      })
      .filter((v) => {
        if (!v._date) {
          console.warn('Registro filtrado por fecha inválida:', v.id);
          return false;
        }
        return true;
      });

    // Combinar ambas fuentes
    const allData = [...normalizedReceipts, ...normalizedVentas];

    // Filtrar por mes/año
    const filtered = allData.filter(
      (r) =>
        r._date.getFullYear() === Number(selectedYear) &&
        r._date.getMonth() + 1 === Number(selectedMonth)
    );

    let totalSalesUSD = 0;
    let totalSalesCOP = 0;
    const salesByTypeUSD = {};
    const salesByTypeCOP = {};
    const salesByPaymentMethodUSD = {};
    const salesByPaymentMethodCOP = {};
    const currencyDiscrepancies = [];
    const dailySalesMap = {};
    const sourceBreakdown = { receipts: 0, ventas: 0 };

    let totalProductCount = 0;
    let receiptCount = 0;
    let totalProfitAmount = 0; // Nueva variable para ganancias totales

    filtered.forEach((r) => {
      const amt = r._amount;
      const curr = r._currency;
      const pay = r._payment;
      const plat = r._platform;
      const day = r._date.getDate();

      // Conteo por fuente
      if (r._source === 'receipts') sourceBreakdown.receipts++;
      else sourceBreakdown.ventas++;

      // Sumar ganancias (solo de Ventas que tienen totalProfit)
      if (r._source === 'Ventas' && r.totalProfit) {
        totalProfitAmount += parseAmount(r.totalProfit);
      }

      if (curr === "USD") {
        totalSalesUSD += amt;
        salesByPaymentMethodUSD[pay] = (salesByPaymentMethodUSD[pay] || 0) + amt;
        salesByTypeUSD[plat] = (salesByTypeUSD[plat] || 0) + amt;
      } else {
        totalSalesCOP += amt;
        salesByPaymentMethodCOP[pay] = (salesByPaymentMethodCOP[pay] || 0) + amt;
        salesByTypeCOP[plat] = (salesByTypeCOP[plat] || 0) + amt;
      }

      // Daily sales
      if (!dailySalesMap[day]) {
        dailySalesMap[day] = { day, USD: 0, COP: 0 };
      }
      dailySalesMap[day][curr] += amt;

      if (typeof r?.cantidadProductos === "number") {
        totalProductCount += r.cantidadProductos;
        receiptCount++;
      } else if (Array.isArray(r?.productosComprados)) {
        totalProductCount += r.productosComprados.length;
        receiptCount++;
      }

      // Discrepancias (solo para receipts)
      if (r._source === 'receipts') {
        const vt = parseAmount(r?.valorTransferido);
        if (Math.abs(vt - amt) > 0.01) {
          currencyDiscrepancies.push({
            id: r.id || r.idmensaje,
            valorCompra: amt,
            valorTransferido: r?.valorTransferido ?? "",
            diferencia: Math.abs(vt - amt),
            currency: curr,
          });
        }
      }
    });

    const averageProductsPerReceipt =
      receiptCount > 0 ? totalProductCount / receiptCount : 0;

    const dailySales = Object.values(dailySalesMap).sort((a, b) => a.day - b.day);

    return {
      totalSalesUSD,
      totalSalesCOP,
      salesByTypeUSD,
      salesByTypeCOP,
      salesByPaymentMethodUSD,
      salesByPaymentMethodCOP,
      averageProductsPerReceipt,
      currencyDiscrepancies,
      dailySales,
      sourceBreakdown,
      totalProfitAmount,
      items: filtered
        .sort((a, b) => b._date - a._date)
        .map((r) => ({
          id: r.id || r.idmensaje,
          fecha: r._date,
          comprador: r?.nombreComprador ?? r?.phoneNumber ?? "",
          medioPago: r._payment,
          plataforma: r._platform,
          currency: r._currency,
          monto: r._amount,
          source: r._source,
          // Campos adicionales de Ventas
          codigo: r?.codigo,
          perfil: r?.perfil,
          investment: r?.investment,
          profit: r?.totalProfit,
        })),
    };
  }, [receiptsData, ventasData, selectedMonth, selectedYear]);

  // Prepare chart data
  const platformChartData = useMemo(() => {
    const data = [];
    Object.entries(processed.salesByTypeUSD).forEach(([name, value]) => {
      data.push({ name, value, currency: 'USD' });
    });
    Object.entries(processed.salesByTypeCOP).forEach(([name, value]) => {
      const existing = data.find(d => d.name === name);
      if (existing) {
        existing.valueCOP = value;
      } else {
        data.push({ name, valueCOP: value, currency: 'COP' });
      }
    });
    return data;
  }, [processed.salesByTypeUSD, processed.salesByTypeCOP]);

  const paymentChartData = useMemo(() => {
    const combined = {};
    Object.entries(processed.salesByPaymentMethodUSD).forEach(([name, value]) => {
      combined[name] = { name, USD: value, COP: 0 };
    });
    Object.entries(processed.salesByPaymentMethodCOP).forEach(([name, value]) => {
      if (combined[name]) {
        combined[name].COP = value;
      } else {
        combined[name] = { name, USD: 0, COP: value };
      }
    });
    return Object.values(combined);
  }, [processed.salesByPaymentMethodUSD, processed.salesByPaymentMethodCOP]);

  const sourceChartData = useMemo(() => [
    { name: 'Receipts', value: processed.sourceBreakdown.receipts, color: '#3b82f6' },
    { name: 'Ventas', value: processed.sourceBreakdown.ventas, color: '#10b981' }
  ], [processed.sourceBreakdown]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#111827', 
      color: '#f3f4f6',
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <header style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{ fontSize: '36px', fontWeight: '600', marginBottom: '8px' }}>
               Estadísticas de Ventas
            </h1>
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>
              <strong>Trabajo y Rectitud</strong>
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Mes</label>
              <select
                style={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  padding: '12px',
                  color: '#f3f4f6',
                  fontSize: '16px'
                }}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Año</label>
              <select
                style={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  padding: '12px',
                  color: '#f3f4f6',
                  fontSize: '16px'
                }}
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </header>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px' }}>
            Cargando datos...
          </div>
        ) : (
          <>
            {/* KPIs */}
            <section style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px',
              marginBottom: '32px'
            }}>
              <div style={{
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '16px',
                padding: '24px',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <DollarSign size={20} color="#10b981" />
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>Total Ventas USD</div>
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#10b981' }}>
                  {processed.totalSalesUSD.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>

              <div style={{
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '16px',
                padding: '24px',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <DollarSign size={20} color="#3b82f6" />
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>Total Ventas COP</div>
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#3b82f6' }}>
                  {processed.totalSalesCOP.toLocaleString("es-CO", {
                    style: "currency",
                    currency: "COP",
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>

              <div style={{
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '16px',
                padding: '24px',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <TrendingUp size={20} color="#f59e0b" />
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>Total Ganancias</div>
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#f59e0b' }}>
                  {processed.totalProfitAmount.toLocaleString("es-CO", {
                    style: "currency",
                    currency: "COP",
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>

              <div style={{
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '16px',
                padding: '24px',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <Database size={20} color="#8b5cf6" />
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>Fuentes de datos</div>
                </div>
                <div style={{ fontSize: '16px', marginTop: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#3b82f6' }}>Receipts:</span>
                    <strong>{processed.sourceBreakdown.receipts}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#10b981' }}>Ventas:</span>
                    <strong>{processed.sourceBreakdown.ventas}</strong>
                  </div>
                </div>
              </div>
            </section>

            {/* Charts */}
            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ fontSize: '26px', fontWeight: '600', marginBottom: '20px', textAlign: 'center' }}>
                 Análisis Visual
              </h2>

              {/* Daily Sales Chart */}
              {processed.dailySales.length > 0 && (
                <div style={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '16px',
                  padding: '24px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                    Ventas Diarias
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={processed.dailySales}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="day" stroke="#9ca3af" label={{ value: 'Día del mes', position: 'insideBottom', offset: -5, fill: '#9ca3af' }} />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#f3f4f6' }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="USD" stroke="#10b981" strokeWidth={2} name="USD" />
                      <Line type="monotone" dataKey="COP" stroke="#3b82f6" strokeWidth={2} name="COP" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                {/* Platform Sales Chart */}
                {platformChartData.length > 0 && (
                  <div style={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '16px',
                    padding: '24px'
                  }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                      Ventas por Plataforma
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={platformChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="name" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#f3f4f6' }}
                        />
                        <Legend />
                        <Bar dataKey="value" fill="#10b981" name="USD" />
                        <Bar dataKey="valueCOP" fill="#3b82f6" name="COP" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Payment Method Chart */}
                {paymentChartData.length > 0 && (
                  <div style={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '16px',
                    padding: '24px'
                  }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                      Ventas por Método de Pago
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={paymentChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="name" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#f3f4f6' }}
                        />
                        <Legend />
                        <Bar dataKey="USD" fill="#10b981" name="USD" />
                        <Bar dataKey="COP" fill="#3b82f6" name="COP" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Source Distribution Pie Chart */}
              {(sourceChartData[0].value > 0 || sourceChartData[1].value > 0) && (
                <div style={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '16px',
                  padding: '24px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', textAlign: 'center' }}>
                    Distribución por Fuente de Datos
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={sourceChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {sourceChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#f3f4f6' }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>

            {/* Discrepancies - Collapsible */}
            {processed.currencyDiscrepancies.length > 0 && (
              <section style={{
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px'
              }}>
                <button
                  onClick={() => setShowDiscrepancies(!showDiscrepancies)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#f3f4f6',
                    fontSize: '18px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  <span>⚠️ Discrepancias ({processed.currencyDiscrepancies.length})</span>
                  {showDiscrepancies ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>

                {showDiscrepancies && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #374151' }}>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>ID</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Moneda</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Valor Compra</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Valor Transferido</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Diferencia</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processed.currencyDiscrepancies.map((d) => (
                            <tr key={d.id} style={{ borderBottom: '1px solid #374151' }}>
                              <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px' }}>{d.id}</td>
                              <td style={{ padding: '12px' }}>{d.currency}</td>
                              <td style={{ padding: '12px' }}>
                                {d.currency === "USD"
                                  ? d.valorCompra.toLocaleString("en-US", { style: "currency", currency: "USD" })
                                  : d.valorCompra.toLocaleString("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '12px' }}>{d.valorTransferido}</td>
                              <td style={{ padding: '12px', color: '#ef4444' }}>
                                {d.currency === "USD"
                                  ? d.diferencia.toLocaleString("en-US", { style: "currency", currency: "USD" })
                                  : d.diferencia.toLocaleString("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Receipts List - Collapsible */}
            <section style={{
              backgroundColor: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <button
                onClick={() => setShowReceiptsList(!showReceiptsList)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  color: '#f3f4f6',
                  fontSize: '18px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                <span>📋 Registros del periodo ({processed.items.length})</span>
                {showReceiptsList ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {showReceiptsList && (
                <div style={{ marginTop: '16px' }}>
                  {processed.items.length === 0 ? (
                    <div style={{ fontSize: '14px', color: '#9ca3af' }}>Sin registros.</div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #374151' }}>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Fuente</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Fecha</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Comprador</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Plataforma</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Medio de pago</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Moneda</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af' }}>Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processed.items.map((it) => (
                            <tr key={it.id} style={{ borderBottom: '1px solid #374151' }}>
                              <td style={{ padding: '12px' }}>
                                <span style={{ 
                                  padding: '4px 8px', 
                                  borderRadius: '4px', 
                                  fontSize: '12px',
                                  backgroundColor: it.source === 'receipts' ? '#1e40af' : '#065f46',
                                  color: '#fff'
                                }}>
                                  {it.source}
                                </span>
                              </td>
                              <td style={{ padding: '12px' }}>{it.fecha.toLocaleString("es-CO")}</td>
                              <td style={{ padding: '12px' }}>{it.comprador}</td>
                              <td style={{ padding: '12px' }}>{it.plataforma}</td>
                              <td style={{ padding: '12px' }}>{it.medioPago}</td>
                              <td style={{ padding: '12px' }}>{it.currency}</td>
                              <td style={{ padding: '12px', fontWeight: '600' }}>
                                {it.currency === "USD"
                                  ? it.monto.toLocaleString("en-US", { style: "currency", currency: "USD" })
                                  : it.monto.toLocaleString("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}